import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  format,
  parseISO,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import {
  GANTT_BAR_ORANGE,
  GANTT_BAR_RED,
  SCHEDULE_COLORS,
} from './scheduleColors';
import GanttScheduleBar from './GanttScheduleBar';
import { normalizeStaffCount } from '../../utils/scheduleDates';

const WEEK_STARTS_MONDAY = 1;

export const GANTT_RANGE_OPTIONS = [7, 14, 30, 60, 90, 180, 365];

const ROW_HEIGHT = 48;
const LABEL_WIDTH = 400;

function dayWidthForRange(rangeDays) {
  if (rangeDays <= 7) return 40;
  if (rangeDays <= 30) return 28;
  if (rangeDays <= 60) return 14;
  if (rangeDays <= 90) return 10;
  if (rangeDays <= 180) return 6;
  return 4;
}

export function alignGanttRangeStart(date) {
  return startOfWeek(startOfDay(date), { weekStartsOn: WEEK_STARTS_MONDAY });
}

export function getGanttRange(rangeStart, rangeDays) {
  const start = alignGanttRangeStart(rangeStart);
  const end = addDays(start, rangeDays - 1);
  return { start, end };
}

function parseYmd(ymd) {
  if (!ymd) return null;
  try {
    return parseISO(String(ymd).split('T')[0]);
  } catch {
    return null;
  }
}

function formatAlertDate(ymd) {
  const date = parseYmd(ymd);
  return date ? format(date, 'M/d') : null;
}

function shouldShowDayLabel(day, dayIndex, rangeDays) {
  if (rangeDays <= 30) return true;
  if (rangeDays <= 60) return day.getDay() === 1 || dayIndex === 0;
  if (rangeDays <= 90) return day.getDate() === 1 || day.getDay() === 1 || dayIndex === 0;
  return day.getDate() === 1 || dayIndex === 0;
}

function ganttRangeButtonLabel(days) {
  return days === 365 ? '1y' : `${days}d`;
}

function scheduleToolbarPillSx(selected) {
  return {
    border: 'none',
    cursor: 'pointer',
    px: 1.25,
    py: 0.5,
    minHeight: 30,
    fontSize: '0.75rem',
    fontWeight: selected ? 700 : 600,
    fontFamily: 'inherit',
    lineHeight: 1.2,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    bgcolor: selected ? 'primary.main' : 'transparent',
    color: selected ? 'primary.contrastText' : 'text.primary',
    '&:hover': {
      bgcolor: selected ? 'primary.dark' : 'action.hover',
    },
  };
}

function ScheduleFilterControls({
  orderFilter,
  onOrderFilterChange,
  showAllPastDue,
  onShowAllPastDueChange,
}) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'stretch',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => onOrderFilterChange('all')}
          aria-pressed={orderFilter === 'all'}
          sx={{
            ...scheduleToolbarPillSx(orderFilter === 'all'),
            borderRight: '1px solid',
            borderColor: 'divider',
          }}
        >
          All active
        </Box>
        <Box
          component="button"
          type="button"
          onClick={() => onOrderFilterChange('withHours')}
          aria-pressed={orderFilter === 'withHours'}
          sx={scheduleToolbarPillSx(orderFilter === 'withHours')}
        >
          With hours
        </Box>
      </Box>
      <Box
        component="button"
        type="button"
        onClick={() => onShowAllPastDueChange(!showAllPastDue)}
        aria-pressed={showAllPastDue}
        sx={{
          ...scheduleToolbarPillSx(showAllPastDue),
          border: '1px solid',
          borderColor: showAllPastDue ? 'primary.main' : 'divider',
          borderRadius: 1,
          bgcolor: showAllPastDue ? 'primary.main' : 'background.paper',
          boxShadow: showAllPastDue
            ? (theme) => `inset 0 0 0 1px ${theme.palette.primary.main}`
            : 'none',
        }}
      >
        Show all past due
      </Box>
    </Stack>
  );
}

function GanttRangeNavigator({ rangeDays, onRangeDaysChange, onRangeNavigate }) {
  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{ ml: { xs: 0, sm: 1 }, flexWrap: 'wrap', rowGap: 0.5 }}
    >
      {GANTT_RANGE_OPTIONS.map((days) => {
        const selected = rangeDays === days;
        const label = ganttRangeButtonLabel(days);
        const dividerColor = selected ? 'primary.main' : 'divider';
        const navButtonSx = {
          borderRadius: 0,
          width: 26,
          height: 26,
          color: selected ? 'primary.main' : 'text.secondary',
          '&:hover': {
            bgcolor: selected ? 'primary.50' : 'action.hover',
          },
        };

        return (
          <Box
            key={days}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              border: '1px solid',
              borderColor: dividerColor,
              borderRadius: 1,
              bgcolor: selected ? 'primary.50' : 'background.paper',
              overflow: 'hidden',
              boxShadow: selected ? (theme) => `inset 0 0 0 1px ${theme.palette.primary.main}` : 'none',
            }}
          >
            <IconButton
              size="small"
              onClick={() => onRangeNavigate?.(days, -1)}
              aria-label={`Previous ${days} days`}
              sx={navButtonSx}
            >
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Box
              component="button"
              type="button"
              onClick={() => onRangeDaysChange?.(days)}
              aria-pressed={selected}
              aria-label={`${label} view`}
              sx={{
                border: 'none',
                borderLeft: '1px solid',
                borderRight: '1px solid',
                borderColor: dividerColor,
                bgcolor: selected ? 'primary.main' : 'transparent',
                color: selected ? 'primary.contrastText' : 'text.primary',
                cursor: 'pointer',
                px: 0.85,
                py: 0.35,
                minWidth: 34,
                fontSize: '0.75rem',
                fontWeight: selected ? 700 : 600,
                fontFamily: 'inherit',
                lineHeight: 1.2,
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                '&:hover': {
                  bgcolor: selected ? 'primary.dark' : 'action.hover',
                },
              }}
            >
              {label}
            </Box>
            <IconButton
              size="small"
              onClick={() => onRangeNavigate?.(days, 1)}
              aria-label={`Next ${days} days`}
              sx={navButtonSx}
            >
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        );
      })}
    </Stack>
  );
}

function formatShortScheduleRange(startYmd, endYmd) {
  const start = parseYmd(startYmd);
  const end = parseYmd(endYmd);
  if (!start || !end) return '';
  const sameYear = start.getFullYear() === end.getFullYear();
  const startFmt = sameYear ? 'M/d' : 'M/d/yy';
  const endFmt = sameYear ? 'M/d' : 'M/d/yy';
  const startStr = format(start, startFmt);
  const endStr = format(end, endFmt);
  if (startStr === endStr) return startStr;
  return `${startStr}–${endStr}`;
}

function formatHoursLabel(hours) {
  if (hours == null || !Number.isFinite(Number(hours))) return 'hrs -';
  const n = Number(hours);
  const value = Number.isInteger(n) ? String(n) : String(n);
  return `hrs ${value}`;
}

function hasEstimatedHours(event) {
  return hasEstimatedHoursValue(event.extendedProps?.estimatedHours);
}

function isActiveOrder(event) {
  const status = String(event.extendedProps?.status || 'open').toLowerCase();
  return status !== 'cancelled' && status !== 'completed' && status !== 'shipped';
}

function scheduleBounds(event) {
  const startYmd = event.extendedProps?.scheduleStart || event.start;
  const endYmd = event.extendedProps?.scheduleEnd;
  return {
    start: parseYmd(startYmd),
    endInclusive: parseYmd(endYmd),
  };
}

/** Row visible when planned schedule overlaps the Gantt window (not due date). */
function scheduleOverlapsWindow(event, windowStart, windowEnd) {
  const { start, endInclusive } = scheduleBounds(event);
  if (!start || !endInclusive) return false;
  return !(endInclusive < windowStart || start > windowEnd);
}

function hasEstimatedHoursValue(hours) {
  return hours != null && Number.isFinite(Number(hours)) && Number(hours) > 0;
}

function isBeforeToday(ymd) {
  const date = parseYmd(ymd);
  if (!date) return false;
  return date < startOfDay(new Date());
}

/** Red = past due date; orange = saved schedule end passed (not shipped). Red wins if both. */
function getUnshippedAlertStyle({ dueDateYmd, scheduleEndYmd, scheduleDatesSaved = true }) {
  if (isBeforeToday(dueDateYmd)) {
    const dueLabel = formatAlertDate(dueDateYmd);
    return {
      barColor: GANTT_BAR_RED,
      rowBg: 'error.50',
      rowHoverBg: 'error.100',
      messageColor: 'error.dark',
      message: dueLabel ? `PAST DUE ${dueLabel}` : 'PAST DUE',
    };
  }
  if (scheduleDatesSaved && isBeforeToday(scheduleEndYmd)) {
    const scheduleEndLabel = formatAlertDate(scheduleEndYmd);
    const dueLabel = formatAlertDate(dueDateYmd);
    const completionPart = scheduleEndLabel
      ? `Work Scheduled for completion ${scheduleEndLabel} but not yet shipped`
      : 'Work Scheduled for completion but not yet shipped';
    const message = dueLabel
      ? `${completionPart} (Past Due ${dueLabel})`
      : completionPart;
    return {
      barColor: GANTT_BAR_ORANGE,
      rowBg: '#fff7ed',
      rowHoverBg: '#ffedd5',
      messageColor: '#c2410c',
      message,
    };
  }
  return null;
}

function formatStaffLabel(staffCount) {
  return `staff: ${normalizeStaffCount(staffCount)}`;
}

function overdueRowMeta(row) {
  const headline = [row.identifier, row.customerName].filter(Boolean).join(' · ');
  const dateRange = formatShortScheduleRange(row.scheduleStart, row.scheduleEnd);
  const hoursLabel = formatHoursLabel(row.estimatedHours);
  const scheduleMeta = [dateRange, hoursLabel].filter(Boolean).join('  ');
  const staffLabel = formatStaffLabel(row.staffCount);
  const alertStyle = getUnshippedAlertStyle({
    dueDateYmd: row.dueDate,
    scheduleEndYmd: row.scheduleEnd,
    scheduleDatesSaved: row.scheduleDatesSaved ?? true,
  });
  return {
    headline,
    description: row.description || '',
    scheduleMeta,
    staffLabel,
    infoMessage: alertStyle?.message || '',
    alertStyle,
    fullTitle: [headline, row.description, scheduleMeta, staffLabel, alertStyle?.message]
      .filter(Boolean)
      .join('\n'),
    workOrderId: row.workOrderId,
  };
}

function computeBarLayout(start, endInclusive, windowStart, windowEnd, dayWidth) {
  if (!start || !endInclusive) return { barLeft: 0, barWidth: 0 };
  const visibleStart = start < windowStart ? windowStart : start;
  const visibleEnd = endInclusive > windowEnd ? windowEnd : endInclusive;
  if (visibleEnd < windowStart || visibleStart > windowEnd) return { barLeft: 0, barWidth: 0 };
  const offsetDays = differenceInCalendarDays(visibleStart, windowStart);
  const spanDays = differenceInCalendarDays(visibleEnd, visibleStart) + 1;
  return {
    barLeft: offsetDays * dayWidth + 2,
    barWidth: Math.max(spanDays * dayWidth - 4, 6),
  };
}

function compareByDueDateAsc(dueA, dueB, tieBreakA = '', tieBreakB = '') {
  const keyA = dueA ? String(dueA).split('T')[0] : '9999-99-99';
  const keyB = dueB ? String(dueB).split('T')[0] : '9999-99-99';
  const cmp = keyA.localeCompare(keyB);
  if (cmp !== 0) return cmp;
  return String(tieBreakA).localeCompare(String(tieBreakB));
}

function orderRowMeta(event) {
  const ep = event.extendedProps || {};
  const identifier = ep.identifier || event.title?.split(' — ')[0] || event.title || '';
  const customer = ep.customerName || '';
  const description = ep.description || '';
  const headline = [identifier, customer].filter(Boolean).join(' · ');
  const scheduleStart = ep.scheduleStart || '';
  const scheduleEnd = ep.scheduleEnd || '';
  const dateRange = formatShortScheduleRange(scheduleStart, scheduleEnd);
  const hoursLabel = formatHoursLabel(ep.estimatedHours);
  const scheduleMeta = [dateRange, hoursLabel].filter(Boolean).join('  ');
  const staffLabel = formatStaffLabel(ep.staffCount);
  return { headline, description, scheduleMeta, staffLabel, fullTitle: event.title };
}

function computeDueDateLineLeft(dueDateYmd, windowStart, windowEnd, dayWidth) {
  const dueDate = parseYmd(dueDateYmd);
  if (!dueDate) return null;
  if (dueDate < windowStart || dueDate > windowEnd) return null;
  const offsetDays = differenceInCalendarDays(dueDate, windowStart);
  return offsetDays * dayWidth + dayWidth / 2;
}

const GANTT_GRID_LINE = 'rgba(0, 0, 0, 0.06)';

function GanttTimelineGrid({ dayWidth, totalDays }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    >
      {Array.from({ length: totalDays }, (_, index) => (
        <Box
          key={index}
          sx={{
            width: dayWidth,
            flexShrink: 0,
            boxSizing: 'border-box',
            borderLeft: `1px solid ${GANTT_GRID_LINE}`,
          }}
        />
      ))}
    </Box>
  );
}

function GanttDueDateLine({ dueDateYmd, windowStart, windowEnd, dayWidth, title }) {
  const left = computeDueDateLineLeft(dueDateYmd, windowStart, windowEnd, dayWidth);
  if (left == null) return null;

  return (
    <Box
      aria-hidden
      title={title || (dueDateYmd ? `Due ${formatAlertDate(dueDateYmd)}` : undefined)}
      sx={{
        position: 'absolute',
        left,
        top: 4,
        bottom: 4,
        width: 2,
        ml: '-1px',
        bgcolor: SCHEDULE_COLORS.dueDateLine,
        borderRadius: 1,
        pointerEvents: 'none',
        zIndex: 1,
      }}
    />
  );
}

function OrderLabelCell({ headline, scheduleMeta, staffLabel, description, fullTitle }) {
  return (
    <Box
      sx={{
        width: LABEL_WIDTH,
        flexShrink: 0,
        px: 1,
        py: 0.5,
        overflow: 'hidden',
      }}
      title={fullTitle}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
        <Typography variant="caption" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
          {headline}
        </Typography>
        {(scheduleMeta || staffLabel) && (
          <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
            {scheduleMeta ? (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ fontSize: '0.7rem', display: 'block', lineHeight: 1.2 }}
              >
                {scheduleMeta}
              </Typography>
            ) : null}
            {staffLabel ? (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ fontSize: '0.7rem', display: 'block', lineHeight: 1.2 }}
              >
                {staffLabel}
              </Typography>
            ) : null}
          </Box>
        )}
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        display="block"
        sx={{ fontSize: '0.7rem', lineHeight: 1.3 }}
      >
        {description || '—'}
      </Typography>
    </Box>
  );
}

export default function ScheduleGanttView({
  events = [],
  overdueSchedule = [],
  allPastDueSchedule = [],
  rangeStart,
  rangeDays = 30,
  onRangeDaysChange,
  onRangeNavigate,
  onEventClick,
  onScheduleChange,
  onGanttInteractionBusyChange,
}) {
  const [orderFilter, setOrderFilter] = useState('all');
  const [showAllPastDue, setShowAllPastDue] = useState(false);
  const { start: windowStart, end: windowEnd } = getGanttRange(rangeStart, rangeDays);
  const days = eachDayOfInterval({ start: windowStart, end: windowEnd });
  const totalDays = days.length;
  const dayWidth = dayWidthForRange(rangeDays);

  const rows = useMemo(() => [...events], [events]);

  const visibleRows = useMemo(() => {
    return rows.filter((event) => {
      if (!isActiveOrder(event)) return false;
      if (orderFilter === 'withHours' && !hasEstimatedHours(event)) return false;
      return scheduleOverlapsWindow(event, windowStart, windowEnd);
    });
  }, [rows, windowStart, windowEnd, orderFilter]);

  const overdueVisible = useMemo(() => {
    const source = showAllPastDue ? allPastDueSchedule : overdueSchedule;
    return (source || []).filter((row) => {
      if (orderFilter === 'withHours' && !hasEstimatedHoursValue(row.estimatedHours)) return false;
      return true;
    });
  }, [allPastDueSchedule, overdueSchedule, orderFilter, showAllPastDue]);

  const displayRows = useMemo(() => {
    const scheduled = visibleRows.map((event) => ({
      kind: 'scheduled',
      key: event.id,
      event,
    }));

    const scheduledIds = new Set(scheduled.map((row) => String(row.event.id)));
    const overdue = overdueVisible
      .filter((row) => !scheduledIds.has(String(row.workOrderId)))
      .map((row) => ({
        kind: 'overdue',
        key: `overdue-${row.workOrderId}`,
        row,
      }));

    return [...scheduled, ...overdue].sort((a, b) => {
      const dueA =
        a.kind === 'scheduled' ? a.event.extendedProps?.dueDate : a.row.dueDate;
      const dueB =
        b.kind === 'scheduled' ? b.event.extendedProps?.dueDate : b.row.dueDate;
      const tieA =
        a.kind === 'scheduled' ? a.event.extendedProps?.identifier : a.row.identifier;
      const tieB =
        b.kind === 'scheduled' ? b.event.extendedProps?.identifier : b.row.identifier;
      return compareByDueDateAsc(dueA, dueB, tieA, tieB);
    });
  }, [visibleRows, overdueVisible]);

  const rangeLabel = `${format(windowStart, 'MMM d, yyyy')} – ${format(windowEnd, 'MMM d, yyyy')} (${rangeDays} days)`;
  const timelineWidth = totalDays * dayWidth;
  const gridMinWidth = LABEL_WIDTH + timelineWidth;
  const headerScrollRef = useRef(null);
  const bodyScrollRef = useRef(null);

  const syncHeaderScrollLeft = useCallback(() => {
    const body = bodyScrollRef.current;
    const header = headerScrollRef.current;
    if (body && header && header.scrollLeft !== body.scrollLeft) {
      header.scrollLeft = body.scrollLeft;
    }
  }, []);

  const handleBodyScroll = useCallback(() => {
    syncHeaderScrollLeft();
  }, [syncHeaderScrollLeft]);

  const handleHeaderScroll = useCallback(() => {
    const body = bodyScrollRef.current;
    const header = headerScrollRef.current;
    if (body && header && body.scrollLeft !== header.scrollLeft) {
      body.scrollLeft = header.scrollLeft;
    }
  }, []);

  const ganttHeaderRow = (
    <Box sx={{ minWidth: gridMinWidth, display: 'flex', bgcolor: 'grey.50' }}>
      <Box sx={{ width: LABEL_WIDTH, flexShrink: 0, px: 1, py: 0.75 }}>
        <Typography variant="caption" fontWeight={600}>
          Order
        </Typography>
      </Box>
      <Box sx={{ width: timelineWidth, flexShrink: 0, display: 'flex' }}>
        {days.map((day, dayIndex) => (
          <Box
            key={day.toISOString()}
            sx={{
              width: dayWidth,
              flexShrink: 0,
              textAlign: 'center',
              py: 0.75,
              borderLeft: `1px solid ${GANTT_GRID_LINE}`,
            }}
          >
            {shouldShowDayLabel(day, dayIndex, rangeDays) ? (
              <>
                {rangeDays <= 30 && (
                  <Typography variant="caption" display="block" lineHeight={1.2}>
                    {format(day, 'EEE')}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ fontSize: rangeDays > 90 ? '0.65rem' : undefined }}
                >
                  {rangeDays > 90 ? format(day, 'MMM') : rangeDays > 60 && day.getDate() === 1
                    ? format(day, 'MMM d')
                    : format(day, 'd')}
                </Typography>
              </>
            ) : null}
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}
      >
        <ScheduleFilterControls
          orderFilter={orderFilter}
          onOrderFilterChange={setOrderFilter}
          showAllPastDue={showAllPastDue}
          onShowAllPastDueChange={setShowAllPastDue}
        />
        <GanttRangeNavigator
          rangeDays={rangeDays}
          onRangeDaysChange={onRangeDaysChange}
          onRangeNavigate={onRangeNavigate}
        />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {rangeLabel}
        </Typography>
      </Stack>

      {displayRows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
          {orderFilter === 'withHours'
            ? `No active orders with estimated hours in this ${rangeDays}-day window. Try another range or add hours on the Work Hrs tab.`
            : `No active orders in this ${rangeDays}-day window. Try another range or set schedule dates on an order's Work Hrs tab.`}
        </Typography>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            maxHeight: 'calc(100vh - 260px)',
            minHeight: 240,
          }}
        >
          <Box
            ref={headerScrollRef}
            onScroll={handleHeaderScroll}
            sx={{
              flexShrink: 0,
              overflowX: 'auto',
              overflowY: 'hidden',
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {ganttHeaderRow}
          </Box>
          <Box
            ref={bodyScrollRef}
            onScroll={handleBodyScroll}
            sx={{
              overflow: 'auto',
              flex: 1,
              minHeight: 0,
            }}
          >
            <Box sx={{ minWidth: gridMinWidth }}>
            {displayRows.map((entry) => {
              if (entry.kind === 'overdue') {
                const meta = overdueRowMeta(entry.row);
                const alertStyle = meta.alertStyle;
                return (
                  <Box
                    key={entry.key}
                    onClick={() =>
                      onEventClick?.({
                        id: String(meta.workOrderId),
                        extendedProps: { workOrderId: meta.workOrderId },
                      })
                    }
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: ROW_HEIGHT,
                      borderBottom: `1px solid ${GANTT_GRID_LINE}`,
                      bgcolor: alertStyle?.rowBg || '#fff7ed',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: alertStyle?.rowHoverBg || '#ffedd5' },
                    }}
                  >
                    <OrderLabelCell
                      headline={meta.headline}
                      scheduleMeta={meta.scheduleMeta}
                      staffLabel={meta.staffLabel}
                      description={meta.description}
                      fullTitle={meta.fullTitle}
                    />
                    <Box
                      data-gantt-timeline
                      sx={{
                        position: 'relative',
                        width: totalDays * dayWidth,
                        flexShrink: 0,
                        minHeight: ROW_HEIGHT,
                        overflow: 'hidden',
                        cursor: 'pointer',
                      }}
                    >
                      <GanttTimelineGrid dayWidth={dayWidth} totalDays={totalDays} />
                      <GanttDueDateLine
                        dueDateYmd={entry.row.dueDate}
                        windowStart={windowStart}
                        windowEnd={windowEnd}
                        dayWidth={dayWidth}
                      />
                      <Box
                        sx={{
                          position: 'relative',
                          zIndex: 1,
                          px: 1,
                          py: 0.5,
                          minHeight: ROW_HEIGHT,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            lineHeight: 1.35,
                            color: alertStyle?.messageColor || '#c2410c',
                          }}
                        >
                          {meta.infoMessage}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              }

              const event = entry.event;
              const ep = event.extendedProps || {};
              const { headline, description, scheduleMeta, staffLabel, fullTitle } = orderRowMeta(event);
              const { start, endInclusive } = scheduleBounds(event);
              if (!start || !endInclusive) return null;

              const { barLeft, barWidth } = computeBarLayout(
                start,
                endInclusive,
                windowStart,
                windowEnd,
                dayWidth
              );
              const alertStyle = getUnshippedAlertStyle({
                dueDateYmd: ep.dueDate,
                scheduleEndYmd: ep.scheduleEnd,
                scheduleDatesSaved: ep.scheduleDatesSaved ?? !ep.usingDefaults,
              });
              const showGrayBar =
                ep.usingDefaults ||
                ep.missingHoursEstimate ||
                !hasEstimatedHoursValue(ep.estimatedHours);
              const color =
                alertStyle?.barColor ||
                (showGrayBar ? SCHEDULE_COLORS.usingDefaults : event.backgroundColor || '#2563eb');
              const showBarLabel = barWidth >= 72;
              const scheduleStartYmd = ep.scheduleStart || format(start, 'yyyy-MM-dd');
              const scheduleEndYmd = ep.scheduleEnd || format(endInclusive, 'yyyy-MM-dd');

              return (
                <Box
                  key={entry.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: ROW_HEIGHT,
                    borderBottom: `1px solid ${GANTT_GRID_LINE}`,
                    bgcolor: alertStyle?.rowBg,
                    '&:hover': alertStyle ? { bgcolor: alertStyle.rowHoverBg } : undefined,
                  }}
                >
                  <OrderLabelCell
                    headline={headline}
                    scheduleMeta={scheduleMeta}
                    staffLabel={staffLabel}
                    description={description}
                    fullTitle={[fullTitle, scheduleMeta, staffLabel].filter(Boolean).join('\n')}
                  />
                  <Box
                    data-gantt-timeline
                    sx={{
                      position: 'relative',
                      width: totalDays * dayWidth,
                      flexShrink: 0,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <GanttTimelineGrid dayWidth={dayWidth} totalDays={totalDays} />
                    <GanttDueDateLine
                      dueDateYmd={ep.dueDate}
                      windowStart={windowStart}
                      windowEnd={windowEnd}
                      dayWidth={dayWidth}
                    />
                    <GanttScheduleBar
                      workOrderId={ep.workOrderId ?? event.id}
                      scheduleStartYmd={scheduleStartYmd}
                      scheduleEndYmd={scheduleEndYmd}
                      windowStart={windowStart}
                      windowEnd={windowEnd}
                      dayWidth={dayWidth}
                      color={color}
                      usingDefaults={showGrayBar}
                      headline={headline}
                      fullTitle={fullTitle}
                      showBarLabel={showBarLabel}
                      onEventClick={() => onEventClick?.(event)}
                      onScheduleChange={onScheduleChange}
                      onInteractionBusyChange={onGanttInteractionBusyChange}
                    />
                    {alertStyle && (
                      <Typography
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          left: Math.max(barLeft + barWidth + 8, 8),
                          fontWeight: 600,
                          color: alertStyle.messageColor,
                          lineHeight: 1.35,
                          whiteSpace: 'nowrap',
                          pointerEvents: 'none',
                        }}
                      >
                        {alertStyle.message}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
